import { useState, useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client/react'
import CREATE_COURSE_PREFILLED from '../../../../../graphql/mutations/courseCatalogue/createCoursePrefilled'
import useUser from '../../../../../hooks/useUser'

import { useLocation, useNavigate, Link } from 'react-router-dom'

import { Form, Container, Row, Col, ListGroup } from "react-bootstrap"
import {
    faUpload,
    faChevronLeft
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import CustomButton from '../../../../../components/CustomButton'

import COLORS from '../../../../../data/colors'
import './index.scss'

import Spinner from "react-bootstrap/Spinner"

import { 
  capitalizeFirstLetter
} from '../../../../../utils/courseCatalogueManager'

import { useTranslation } from 'react-i18next'


const UploadCourse = () => {
    
  const { t } = useTranslation('translation', {keyPrefix: "courseCatalogue.coursesSection.uploadSection"})

  const { user } = useUser()

  const navigate = useNavigate()

  const formRef = useRef(null)

  const [file, setFile] = useState(null)
  const [selectedFileError, setSelectedFileError] = useState([])
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadResults, setUploadResults] = useState({})
  const [uploadHasError, setUploadHasError] = useState(false)

  const [validated, setValidated] = useState(false)

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [createCoursePrefilled, { 
      loading: loadingCreateCoursePrefilled, 
      error: errorCreateCoursePrefilled, 
      data: dataCreateCoursePrefilled
    }] = useMutation(
    CREATE_COURSE_PREFILLED, {
      onCompleted: (data) => {
        console.log("CSV uploaded")
        setUploadResults(data.createCoursePrefilled)

        if(data?.createCoursePrefilled?.success) {
          setUploadSuccess(true)
          setUploadHasError(false)
          setValidated(false)
        } else {
          setUploadHasError(true)
          setUploadSuccess(false)
          setValidated(false)
        }
      },
      onError: (error) => {
        console.error("Error CSV upload")
      }
    }
  )

  useEffect(() => {
    
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))

    if(!userHasPermission) {
      navigate('/cc')
      return
    }

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

  }, [])


  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) {
      setSelectedFileError([t('selectedFileError.CSVRequired')])
      setFile(null)
    } else if (!selectedFile.name.endsWith(".csv")) {
      setSelectedFileError([t('selectedFileError.onlyCSVAllowed')])
      setFile(null)
    } else {
      setSelectedFileError([])
      setFile(selectedFile)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadSuccess(false)
    setUploadHasError(false)

    if (!formIsValidated(e)) return

    if (!file) {
      setSelectedFileError([t('selectedFileError.CSVRequired')])
      return
    }

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    try {
      const arrayBuffer = await file.arrayBuffer()
      let binary = ''
      const bytes = new Uint8Array(arrayBuffer)
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      const fileBase64 = btoa(binary)

      let result = await createCoursePrefilled({
        variables: {
          courseFile: fileBase64,
          baseUrl,
          apiKey,
          schacCode: user?.heiID,
        },
      })

      if(uploadResults?.success) {
        setUploadSuccess(true)
        setUploadHasError(false)
        setValidated(false)
      } else {
        setUploadHasError(true)
        setUploadSuccess(false)
        setValidated(false)
      }
    } catch (err) {
      console.error("Upload failed")
      setValidated(false)
    }
  }

  const isFormReady = Boolean(file)

  return (
    <Container className="mt-5 pt-3">
      <Row className="mb-5">
        <Link to="/cc/courses" className="goBackLink">
          <FontAwesomeIcon
            icon={faChevronLeft}
            color={COLORS.greyText}
            className="with-icon me-1"
            id={"smallIcon"}
          />
          {t('goBack')}
        </Link>
      </Row>
      <Row className="align-items-center">
        <Col>
          <Form 
            ref={formRef} 
            noValidate 
            validated={validated} 
            id={"upload-course-form"}
            onSubmit={handleSubmit}
          >
            <h3 className='h3-upload mb-5'>
              {t('title')}
            </h3>

            <p className='lh-base mb-4'>
              {t('subtitle.part1')}
              <a href="https://templates.cc.uni-foundation.eu/" target="_blank" rel="noreferrer">{t('subtitle.part2')}</a>.
            </p>
            
            <Form.Group controlId="formFile" className="my-3">
              <Form.Control type="file" accept='.csv' onChange={handleFileChange} required />
              <Form.Control.Feedback type="invalid">
                {selectedFileError.map((msg, i) => (
                  <div key={i} className="text-danger">
                    {msg}
                  </div>
                ))}
              </Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col style={{ marginTop: 36, display: "flex", justifyContent: "flex-end" }}>
                <CustomButton 
                  buttonType={"primary"} 
                  small={true} 
                  text={loadingCreateCoursePrefilled ? t('uploading') : t('upload')} 
                  hasIcon={true} 
                  iconColor={COLORS.white} 
                  icon={faUpload} 
                  disabled={!isFormReady}
                  handleClick={() => formRef.current?.requestSubmit()}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      {loadingCreateCoursePrefilled &&
        <Row className="align-items-center mt-5">
          <div className="text-center">
            <Spinner
              as="div"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="align-middle my-auto me-2"
            />
            <span>{t('uploadingCSVFile')}</span>
          </div>
        </Row>
      }

      {selectedFileError.length > 0 &&
        (
          <Row className="mt-5 align-items-center">
            <p className="text-danger text-center">
              {selectedFileError}
            </p>
          </Row>
        )
      }
      
      {errorCreateCoursePrefilled && 
        (
          <Row className="mt-5 align-items-center">
            <p className="text-danger text-center">
              {capitalizeFirstLetter(errorCreateCoursePrefilled?.message)}
            </p>
          </Row>
        )
      }

      {uploadHasError && uploadResults?.errors &&
        (
          <Row className="my-5 align-items-center">
            <Col md={10} className="p-4 text-center mx-auto">
              <p className="mb-4 lead">{t('CSVerror')}</p>
              <ListGroup>
                {uploadResults?.errors.map((err, i) => (
                  <ListGroup.Item key={i}>
                  <p className="my-3">
                    {err.row && (
                      <>
                        <span className="fw-bold">{`${t('row')} ${err.row}`}</span>:
                      </>
                    )}
                    <span className="ms-2 me-2">{err.message}</span>
                    <span className="fw-bold">{t('column')}</span>:
                    <span className="ms-2">{err.path}</span>
                  </p>
                </ListGroup.Item>
                ))}
              </ListGroup>
            </Col>
          </Row>
        )
      }

      {(uploadSuccess || dataCreateCoursePrefilled?.createCoursePrefilled?.success) && !loadingCreateCoursePrefilled &&
        <Row className="align-items-center mt-5">
          <Col>
            <p className="text-success text-center">{t('uploadedCSV')}</p>
          </Col>
        </Row>
      }
    </Container>
  )
}

export default UploadCourse